import type { ReactNode } from "react";
import { Link } from "react-router";

import { cx } from "@/lib/cx";

/**
 * Story 15.8's own new, small shared component (spec Judgement call 5): a
 * horizontal, 4-stat strip rendered directly beneath a record page's own
 * header, above the existing "primary fields immediately / rest behind
 * More details" grouped list — the Property and Tenant record pages are
 * its first two callers, each supplying its own four `{label, value}`
 * pairs. Deliberately not a reuse of `components/dashboard/StatTile.tsx`:
 * that primitive is explicitly scoped "Dashboard only" (DESIGN.md §
 * Components `stat-tile`), and this strip also needs an affordance
 * `StatTile` has no shape for — an optional link (the Tenant strip's own
 * "property/unit" stat links to the property's record page) and an
 * optional inline badge (the Property strip's own compliance-deadline stat
 * marks an overdue date plainly, mirroring `BackupStatus.tsx`'s own
 * established `is_overdue` -> `Badge` precedent).
 *
 * Layout (VC-15.8-09, UX-DR22): two columns by default — this codebase's
 * own established phone-first rule (DESIGN.md: never a horizontally
 * -scrolling row) — widening to four across at the `md` breakpoint, the
 * one breakpoint every other responsive layout in this codebase already
 * uses (`RecordPage.tsx`'s own header, `FieldRow.tsx`'s own label/value
 * split). Plain relative units throughout (a CSS grid plus `rem`-scaled
 * type via the existing `text-meta`/`text-body` utilities), so the strip
 * reflows rather than overflows at 200% browser zoom.
 */
export interface KeyFact {
  label: string;
  value: string;
  /** A working link (mirrors `NextThirtyDaysRow.tsx`'s own `entity_url`
   * pattern) — absent renders plain text. */
  href?: string;
  /** An inline marker beside the value — today only the Property strip's
   * own overdue compliance-deadline `Badge` uses this. */
  badge?: ReactNode;
  /**
   * Story 19.2 — this fact's value is a primary, glanceable currency figure
   * (the Property/Tenant strips' own "Scheduled rent"/"Monthly rent" stat),
   * so it renders with the money accent instead of the strip's plain `ink`
   * value color. Every other fact in this strip (dates, tenant names, the
   * property/unit link) stays untinted — this is a per-fact opt-in, not a
   * strip-wide style, since a fact strip mixes money and non-money stats.
   */
  tone?: "money";
}

export interface KeyFactsStripProps {
  facts: KeyFact[];
}

export function KeyFactsStrip({ facts }: KeyFactsStripProps) {
  // Live-caught (Property's own 5-fact strip, `propertyKeyFacts`): a real
  // stat count beyond this component's own documented 4-per-row design left
  // the 5th tile stranded alone in an otherwise-empty row. Widens only the
  // single tile that would otherwise be alone in the last row — the common
  // real case (one fact more than a clean 4) — rather than reflowing every
  // caller's grid to a fixed 5 columns, which would make Tenant's own
  // always-exactly-4 strip needlessly wide.
  const lastIsOrphaned = facts.length % 4 === 1;
  return (
    <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
      {facts.map((fact, index) => (
        <div
          key={fact.label}
          className={cx(
            "rounded-lg border border-border-hairline bg-surface p-4",
            lastIsOrphaned && index === facts.length - 1 ? "col-span-2 md:col-span-4" : undefined,
          )}
        >
          <p className="text-meta text-ink-secondary">{fact.label}</p>
          <p
            className={cx(
              "mt-1 flex flex-wrap items-center gap-2 text-body font-semibold",
              fact.tone === "money" ? "text-money" : "text-ink",
            )}
          >
            {fact.href ? (
              <Link to={fact.href} className="text-accent underline">
                {fact.value}
              </Link>
            ) : (
              <span>{fact.value}</span>
            )}
            {fact.badge ?? null}
          </p>
        </div>
      ))}
    </div>
  );
}
